/* eslint-disable react/prop-types */
import currency from "currency.js";
import moment from "moment";
import { TFunction } from "i18next";
import {
  GetOrders200Response,
  InstrumentTypeResponse,
  MarginTypeResponse,
  OrderTypeResponse,
} from "../../codegen-api";
import { SPACING } from "../../constants/design/spacing";
import { COLORS } from "../../constants/design/colors";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { epochSecondsFrom, getTimeAgo } from "../../utils/date";
import { formatSizePrecision } from "../../utils/format";
import { getOptionsTypeFromSymbol } from "../../utils/instruments";
import MarketCell from "../PortfolioSettings/Table/MarketCell";
import RowActionButton from "../shared/RowActionButton";
import {
  Align,
  ButtonGroup,
  Direction,
  SizeFillCell,
} from "../shared/Table/style";
import { CancelAllButton, OrderGroupedCells } from "./style";
import { IModifiedOrder } from "./types";
import { UNKNOWN_DISPLAY } from "../../utils/unknownNumbers";

export function getOrdersColumns(
  t: TFunction,
  tooltip: TFunction,
  selectedMarketType: InstrumentTypeResponse | undefined,
  resolveInstrumentName: (instrument?: any) => string | undefined,
  resolveMarketPrecision: (instrument?: any) => any,
  getInstrumentLeverage: (instrumentId: string) => any,
  translateAll: TFunction,
  onClickCancelAll: () => void,
  onCancelOrder: (order?: GetOrders200Response) => void,
  onEditOrder: (order?: GetOrders200Response) => void
): ITableColumn<IModifiedOrder>[] {
  const ivCols: ITableColumn<IModifiedOrder>[] = [
    {
      title: t("iv"),
      accessor: "iv",
      align: "right",
      tooltip: tooltip("iv"),
      valueExtractor: (value) =>
        value ? `${(Number(value) * 100).toFixed(2)}%` : "-",
    },
  ];
  const cols: ITableColumn<IModifiedOrder>[] = [
    {
      title: t("market"),
      accessor: "instrument_name",
      tooltip: tooltip("market"),
      Cell: ({ row }) => {
        const { expiry, strike, instrument_name, instrument_id } =
          row.original;
        const optionType = getOptionsTypeFromSymbol(instrument_name);
        const leverage = getInstrumentLeverage(instrument_id)?.leverage;

        const displayName =
          resolveInstrumentName(row.original) || instrument_name;
        if (optionType) {
          return (
            <MarketCell
              instrumentName={displayName}
              optionType={optionType}
              expiry={expiry}
              strike={strike}
            />
          );
        }

        return (
          <MarketCell
            leverage={Number(leverage)}
            instrumentName={displayName}
          />
        );
      },
    },
    {
      title: t("order_id"),
      accessor: "order_id",
      align: "left",
      Cell: ({ value }) => (
        // An order id is opaque: the table's inherited `text-transform:
        // capitalize` rendered lowercase hex as "Dbb20b4b...", so an id copied
        // out of the UI did not match the one on chain.
        <span
          style={{ textTransform: "none" }}
          title={value ? `Order ID ${value}` : undefined}
        >
          {value ? `#${value}` : "-"}
        </span>
      ),
    },
    {
      title: t("side"),
      align: "left",
      accessor: "side",
      tooltip: tooltip("side"),
      Cell: ({ value }) => <Direction side={value}>{t(value)}</Direction>,
    },
    {
      title: t("type"),
      align: "left",
      accessor: "type",
      tooltip: tooltip("orderType"),
      Cell: ({ value }) => <span>{value}</span>,
    },
    {
      title: t("size_fill"),
      align: "right",
      accessor: "amount",
      tooltip: tooltip("orderSize"),
      Cell: ({ row }) => (
        <Align align="right">
          <SizeFillCell>
            <span>
              {row.original.close_position === true
                ? t("close_position")
                : formatSizePrecision(
                    row.original.amount,
                    row.original.amountPrecision
                  )}
            </span>
            <span>
              {formatSizePrecision(
                row.original.filled,
                row.original.amountPrecision
              )}
            </span>
          </SizeFillCell>
        </Align>
      ),
    },
    {
      title: t("price_trigger"),
      accessor: "price",
      align: "right",
      tooltip: tooltip("priceTrigger"),
      Cell: ({ value, row }) => {
        const { trigger, order_type, instrument_name, instrument_type } =
          row.original;
        const precision = resolveMarketPrecision({
          instrument_id: row.original.instrument_id,
          instrument_name,
          instrument_type,
        }).price_precision;
        return (
          <SizeFillCell>
            <span>
              {order_type === OrderTypeResponse.Market
                ? "-"
                : currency(value, { precision }).format()}
            </span>
            <span>
              {trigger ? currency(trigger, { precision }).format() : "-"}
            </span>
          </SizeFillCell>
        );
      },
    },
    {
      title: t("value"),
      accessor: "price",
      id: "usdcValue",
      align: "right",
      Cell: ({ row }: any) => {
        const { amount, price, order_type } = row.original;
        const rawAmount = Number(amount || 0);
        // amount may be in 18-decimal wei format (same logic as formatSizePrecision)
        const parsedAmount = rawAmount > 1e12 ? rawAmount / 1e18 : rawAmount;
        const usdcValue = parsedAmount * Number(price || 0);
        return (
          <Align align="right">
            {order_type === OrderTypeResponse.Market || !price
              ? "-"
              : currency(usdcValue).format()}
          </Align>
        );
      },
    },
    ...(selectedMarketType === InstrumentTypeResponse.Option ? ivCols : []),
    {
      title: t("initial_margin"),
      accessor: "initial_margin",
      align: "right",
      tooltip: tooltip("initialMargin"),
      Cell: ({ value, row }) => {
        if (
          getInstrumentLeverage(row.original.instrument_id)?.margin_type ===
          MarginTypeResponse.Isolated
        ) {
          return (
            <Align align="right">
              <div style={{ flexDirection: "column" }}>
                <div>{currency(Number(value || 0)).format()}</div>
                <div style={{ color: COLORS.blue.one }}>{t("isolated")}</div>
              </div>
            </Align>
          );
        }

        return (
          <Align align="right">{currency(Number(value || 0)).format()}</Align>
        );
      },
    },
    {
      title: t("time"),
      accessor: "created_timestamp",
      align: "right",
      tooltip: tooltip("orderTimestamp"),
      valueExtractor: (value) =>
        (() => {
          // The API sends this in NANOseconds. Read as seconds it lands
          // ~56bn years ahead, and getTimeAgo calls any future date
          // "just now" -- so every row read "Just Now", four-month-old
          // orders included.
          const seconds = epochSecondsFrom(value as string | number);
          return seconds === undefined
            ? UNKNOWN_DISPLAY
            : getTimeAgo(moment.unix(seconds), translateAll);
        })(),
    },
    {
      id: "rowActionsSideTypExpiry",
      Header: () => (
        <OrderGroupedCells>
          <CancelAllButton onClick={onClickCancelAll}>
            Cancel All
          </CancelAllButton>
        </OrderGroupedCells>
      ),
      Cell: ({ row }: any) => (
        <OrderGroupedCells>
          <ButtonGroup>
            <RowActionButton
              variant={"close"}
              onClick={(e) => {
                e.stopPropagation();
                onCancelOrder(row.original as GetOrders200Response);
              }}
              style={{ marginRight: `${SPACING.two}px` }}
            />
            {/* {!row.original.close_position && (
              <RowActionButton
                variant={"edit"}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditOrder(row.original as GetOrders200Response);
                }}
                style={{ marginRight: `${SPACING.two}px` }}
              />
            )}
            <RowActionButton
              variant={"details"}
              onClick={(e) => {
                e.stopPropagation();
                setViewDetailsForOrder(row.original as GetOrders200Response);
              }}
            /> */}
          </ButtonGroup>
        </OrderGroupedCells>
      ),
    },
  ];
  return cols;
}
